export type UserRole = 'admin' | 'vendor' | 'organizer' | 'client';

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  paroisse?: string | null;
  diocese_prefere?: string | null;
  avatar?: string | null;
  role: UserRole;
  vendor_application?: {
    shop_name?: string | null;
    location?: string | null;
    category?: string | null;
    bio?: string | null;
  } | null;
}

export interface RegisterRequestPayload {
  name: string;
  email: string;
  phone?: string;
  paroisse?: string;
  diocese_prefere?: string;
  role: 'vendor' | 'organizer' | 'client';
  password: string;
  password_confirmation: string;
}

export interface RegisterResponse {
  user: AuthUser;
  token?: string;
  approval_required?: boolean;
}

type ApiEnvelope<T> = {
  data?: T;
  message?: string;
};
const API_BASE = import.meta.env.VITE_API_BASE_URL;
// const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? 'https://foi-sn.mmebov.org').replace(/\/$/, '');
const BASE_URL = `${API_BASE}/api`;
if (!API_BASE) {
  throw new Error("VITE_API_BASE_URL n'est pas défini");
}
const TOKEN_KEY = 'fsn_web_token';
const USER_KEY = 'fsn_web_user';

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function storeAuth(token: string, user: AuthUser) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function storeUser(user: AuthUser) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearStoredAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getApiBaseUrl() {
  return API_BASE;
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getStoredToken();
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');

  if (!(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {

      ...init,
      headers,
    });
  } catch {
    throw new Error('Activez votre connexion internet.');
  }

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as ApiEnvelope<T> & { errors?: Record<string, string[]>; message?: string }) : {};

  if (!response.ok) {
    const message =
      payload.message ??
      Object.values(payload.errors ?? {}).flat()[0] ??
      'Une erreur est survenue.';
    throw new Error(message);
  }

  return ((payload.data ?? payload) as T);
}

export async function loginRequest(email: string, password: string) {
  return apiRequest<{ user: AuthUser; token: string }>('/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function logoutRequest() {
  return apiRequest<{ message?: string }>('/logout', {
    method: 'POST',
  });
}

export async function registerRequest(payload: RegisterRequestPayload | FormData) {
  return apiRequest<RegisterResponse>('/register', {
    method: 'POST',
    body: payload instanceof FormData ? payload : JSON.stringify(payload),
  });
}
